/**
 * An inline import area (W2-09 publish-import; DESIGN.md §5.12): says that charts, packages and folders can be
 * dropped anywhere (the shell's global drop zone hands every drop to this package's file-drop handlers) and offers
 * the two pickers a browser has: files, and a whole folder. It highlights while files are dragged over it but leaves
 * the drop itself to the global drop zone, so a drop here and a drop elsewhere behave the same.
 */

import { useState, type DragEvent } from 'react';
import { Button } from '../../ui/Button';
import { Icon } from '../../ui/Icon';
import type { Text } from './hooks';
import { m } from './import.messages';
import s from './Import.module.css';

export interface DropZoneProps {
  readonly text: Text;
  /** Opens the file picker. */
  readonly onChooseFiles: () => void;
  /** Opens the folder picker. */
  readonly onChooseFolder: () => void;
}

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer.types).includes('Files');
}

export function DropZone(props: DropZoneProps) {
  const { text } = props;
  const [over, setOver] = useState(false);
  return (
    <div
      className={s.dropZone}
      data-over={String(over)}
      data-help-id="import.dropZone"
      onDragEnter={(e) => {
        if (hasFiles(e)) setOver(true);
      }}
      onDragLeave={(e) => {
        if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) setOver(false);
      }}
      onDrop={() => {
        setOver(false);
      }}
    >
      <Icon name="FileInput" size={24} />
      <span>{text.t(m.dropHere)}</span>
      <div className={s.dropButtons}>
        <Button icon="FileUp" onClick={props.onChooseFiles}>
          {text.t(m.chooseFiles)}
        </Button>
        <Button icon="FolderUp" onClick={props.onChooseFolder}>
          {text.t(m.chooseFolder)}
        </Button>
      </div>
    </div>
  );
}
